import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { API_CONFIG } from '../config/env';
import {
  CheckInReminderOptOut,
  CreateTherapyCheckInRequest,
  MyPrescriptionControllerApi,
  MyTherapyCheckIns as StimHomeMyCheckIns,
  MyTherapyCheckInControllerApi,
  Prescription as StimHomePrescription,
  TherapyCheckIn,
  UpdateTherapyCheckInRequest,
} from '../generated/stim-home';
import {
  QK_STIM_HOME_CHECK_IN_REMINDER,
  QK_STIM_HOME_MY_CHECK_IN_HISTORY,
  QK_STIM_HOME_MY_PRESCRIPTION_LIST,
  QK_STIM_HOME_PRESCRIPTION_BY_ID,
} from '../query-keys';

export type { Prescription as StimHomePrescription } from '../generated/stim-home';
export type { CheckInReminderOptOut as StimHomeCheckInReminder } from '../generated/stim-home';
export type { MyTherapyCheckIns as StimHomeMyCheckIns, TherapyCheckIn as StimHomeCheckIn } from '../generated/stim-home';
export type {
  CreateTherapyCheckInRequest as StimHomeCheckInCreateRequest,
  UpdateTherapyCheckInRequest as StimHomeCheckInUpdateRequest,
} from '../generated/stim-home';
export { CheckInCompletion as StimHomeCheckInCompletion } from '../generated/stim-home';
export { CheckInDiscomfort as StimHomeCheckInDiscomfort } from '../generated/stim-home';
export { CheckInIncompleteReason as StimHomeCheckInIncompleteReason } from '../generated/stim-home';
export { PrescriptionStatus as StimHomePrescriptionStatus } from '../generated/stim-home';

const STIM_HOME_URL = API_CONFIG.STIM_HOME_URL;
// 내 처방 조회는 MyPrescriptionControllerApi(/api/v1/me/prescriptions) 가 담당한다.
const myPrescriptionControllerApi = new MyPrescriptionControllerApi(undefined, STIM_HOME_URL);
const myTherapyCheckInControllerApi = new MyTherapyCheckInControllerApi(undefined, STIM_HOME_URL);

export const useFetchMyPrescriptions = () => {
  return useQuery<StimHomePrescription[]>({
    queryKey: [QK_STIM_HOME_MY_PRESCRIPTION_LIST],
    queryFn: () => myPrescriptionControllerApi.findMyPrescriptions().then((res) => res.data),
  });
};

/**
 * prescriptionId 로 환자 본인의 처방 단건 조회 (GET /api/v1/me/prescriptions/{prescriptionId}).
 * 없는 id·삭제된 처방·남의 처방은 서버가 모두 404 로 답한다(존재 여부를 캐지 못하게 하기 위함).
 */
export const useFetchPrescriptionById = (prescriptionId?: string | null) => {
  return useQuery<StimHomePrescription, AxiosError>({
    queryKey: [QK_STIM_HOME_PRESCRIPTION_BY_ID, prescriptionId],
    queryFn: () => myPrescriptionControllerApi.findMyPrescriptionById(prescriptionId!).then((res) => res.data),
    enabled: !!prescriptionId,
  });
};

/**
 * 처방의 치료 체크인 내역 조회 (GET /api/v1/me/prescriptions/{prescriptionId}/check-ins).
 * 작성한 기록(checkIns)과 함께 새로 쓸 수 있는 날(writableDates)·고칠 수 있는 날(editableDates)을 준다.
 */
export const useFetchMyCheckInHistory = (prescriptionId?: string | null) => {
  return useQuery<StimHomeMyCheckIns, AxiosError>({
    queryKey: [QK_STIM_HOME_MY_CHECK_IN_HISTORY, prescriptionId],
    queryFn: () => myTherapyCheckInControllerApi.findHistory(prescriptionId!).then((res) => res.data),
    enabled: !!prescriptionId,
  });
};

/** 치료 체크인 작성 (POST /api/v1/me/prescriptions/{prescriptionId}/check-ins). */
export const useCreateMyCheckIn = (prescriptionId?: string | null) => {
  const queryClient = useQueryClient();
  return useMutation<TherapyCheckIn, AxiosError, CreateTherapyCheckInRequest>({
    mutationFn: (body) => myTherapyCheckInControllerApi.create1(prescriptionId!, body).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QK_STIM_HOME_MY_CHECK_IN_HISTORY, prescriptionId] });
    },
  });
};

/**
 * 치료 체크인 리마인더 수신 설정 조회 (GET /api/v1/me/check-in-reminder-opt-out).
 * 서버는 '수신 거부(optedOut)' 기준이라 화면의 알림 on/off 와 뜻이 반대다.
 */
export const useFetchMyCheckInReminder = () => {
  return useQuery<CheckInReminderOptOut, AxiosError>({
    queryKey: [QK_STIM_HOME_CHECK_IN_REMINDER],
    queryFn: () => myTherapyCheckInControllerApi.findReminderOptOut().then((res) => res.data),
  });
};

/** 치료 체크인 리마인더 수신 설정 변경 (PUT /api/v1/me/check-in-reminder-opt-out). */
export const useUpdateMyCheckInReminder = () => {
  const queryClient = useQueryClient();
  return useMutation<CheckInReminderOptOut, AxiosError, boolean>({
    mutationFn: (optedOut) =>
      myTherapyCheckInControllerApi.updateReminderOptOut({ optedOut }).then((res) => res.data),
    // 응답이 변경 후 상태라 재조회 대신 캐시를 갈아끼운다.
    onSuccess: (data) => queryClient.setQueryData([QK_STIM_HOME_CHECK_IN_REMINDER], data),
  });
};

/** 치료 체크인 수정 (PUT /api/v1/me/check-ins/{checkInId}). */
export const useUpdateMyCheckIn = (prescriptionId?: string | null) => {
  const queryClient = useQueryClient();
  return useMutation<TherapyCheckIn, AxiosError, { checkInId: string; body: UpdateTherapyCheckInRequest }>({
    mutationFn: ({ checkInId, body }) =>
      myTherapyCheckInControllerApi.update(checkInId, body).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QK_STIM_HOME_MY_CHECK_IN_HISTORY, prescriptionId] });
    },
  });
};
